import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { FloorAssemblyOverride, GeneratedFloorBoundaries } from "building-types/floors/floorAssemblyTypes";
import type { FloorSystemExternal } from "building-types/floors/floorSystemTypes";
import type { FoundationAssemblyEntityDatums } from "building-types/foundations/foundationAssemblyEntityDatumsTypes";
import type { FoundationAssemblyEntity } from "building-types/foundations/foundationAssemblyEntityTypes";
import type { FoundationComponentEntity } from "building-types/foundations/foundationComponentEntityTypes";
import { FoundationCondition, FoundationSuperSystemType } from "building-types/foundations/foundationEnums";
import { NONE_VALUE } from "building-types/foundations/foundationEnums";
import type { Foundation, FoundationSystem } from "building-types/foundations/foundationSystemTypes";
import { type FoundationPath, type HalfEdgeToFoundationPath } from "building-types/foundations/foundationTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { FoundationSettings } from "building-types/settings/foundationSettings";
import type { FoundationSystemSettings } from "building-types/settings/foundationSettingsTypes";
import type { RWTable, Table } from "common/data/table";
import { finalizeFoundationComponents } from "doc/elements/foundation/foundationComponentsTools";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { evalFormulas } from "doc/formula/evalFormulas";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { getEdgeIdsForPerimeter } from "../elements/floor/floor";
import { makeSolidFoundationAssembly } from "../elements/foundation/foundationAssemblyTools";
import {
  foundationPerimeterInfoToUse,
  getTransitionEdgeSourceId,
  isFoundationTransitionEdge,
} from "../elements/foundation/foundationMirrorUtils";
import { foundationPathOpposite } from "../elements/foundation/foundationStrip";
import { shouldProcessFloorWithBoundaries } from "./generateFloorHorizontalAssembliesTools";

function defaultHorizontalAssemblyFoundationCondition(
  foundationSettings: FoundationSystemSettings,
): FoundationCondition {
  if (foundationSettings.foundationSuperSystemType === FoundationSuperSystemType.BASEMENT) {
    return FoundationCondition.BASEMENT_EXTERIOR;
  }
  return FoundationCondition.HOUSE_EXTERIOR;
}

export interface GenerateFoundationHorizontalAssembliesPathsInput {
  foundationComponents: Table<FoundationComponentEntity>;
  foundationAssemblies: Table<FoundationAssemblyEntity>;
  foundationSystems: Table<FoundationSystem>;
  foundations: Table<Foundation>;
  floorSystems: Table<FloorSystemExternal>;
  floors: Table<FloorAssemblyOverride>;
  floorBoundaries: Table<GeneratedFloorBoundaries>;
  datumAbstractions: Table<DatumAbstraction>;
  foundationSettings: FoundationSettings;
  optionChoices: OptionChoices;
  entityDisabledByGroup: Table<boolean>;
  planarGraphs: Table<PolygonPlanarGraph>;
  mirror: boolean;
  mirrorTranslationX: number;
}

/**
 * Generates foundation horizontal assemblies paths from foundations.
 */
export function generateFoundationHorizontalAssembliesPathsFromFoundations(
  input: GenerateFoundationHorizontalAssembliesPathsInput,
): RWTable<HalfEdgeToFoundationPath> {
  const {
    foundationComponents,
    foundationAssemblies,
    foundationSystems,
    foundations,
    datumAbstractions,
    foundationSettings,
    optionChoices,
    entityDisabledByGroup,
    planarGraphs,
    mirror,
    mirrorTranslationX,
  } = input;

  const foundationSettingsFromConfig = foundationSystemSettingsFromSettingsConfig(foundationSettings);

  const finalizedFoundationComponents = finalizeFoundationComponents(
    foundationComponents,
    foundationSettingsFromConfig,
  );

  const foundationHorizontalAssembliesPaths: RWTable<HalfEdgeToFoundationPath> = {};

  const resolvedFoundationSystems = evalFormulas(
    foundationSystems,
    (v) => optionChoices[v],
    undefined,
    entityDisabledByGroup,
  );
  const resolvedFoundations = evalFormulas(foundations, (v) => optionChoices[v], undefined, entityDisabledByGroup);

  for (const foundationId in resolvedFoundations) {
    const foundation = resolvedFoundations[foundationId];
    if (!foundation || foundation.disabled) {
      continue;
    }
    const foundationSystemId = foundation.parentId;
    if (resolvedFoundationSystems[foundationSystemId]?.disabled) {
      continue;
    }

    const { perimeter, edgeIds } = foundation;

    if (!perimeter || !edgeIds) {
      continue;
    }

    const foundationGraph = planarGraphs[foundationId];
    if (!foundationGraph) {
      console.error(`Foundation graph not found for foundation ${foundationId}`);
      continue;
    }

    const foundationPaths = generateFoundationPathsFromFoundationPerimeter({
      planarGraphId: foundationId,
      planarGraph: foundationGraph,
      planarGraphs,
      foundation,
      foundationAssemblies,
      finalizedFoundationComponents,
      foundationSettings: foundationSettingsFromConfig,
      datumAbstractions,
      polygonIndex: 0,
      mirror,
      mirrorTranslationX,
    });

    if (Object.keys(foundationPaths).length === 0) {
      console.error(`Foundation paths not found for foundation ${foundationId}`);
      continue;
    }

    foundationHorizontalAssembliesPaths[foundationId] = foundationPaths;
  }

  return foundationHorizontalAssembliesPaths;
}

/**
 * Generates foundation horizontal assemblies paths from floors using only perimeter.
 */
export function generateFoundationHorizontalAssembliesPathsFromFloors(
  input: GenerateFoundationHorizontalAssembliesPathsInput,
): RWTable<HalfEdgeToFoundationPath> {
  const {
    foundationComponents,
    foundationAssemblies,
    floorSystems,
    floors,
    floorBoundaries,
    datumAbstractions,
    foundationSettings,
    optionChoices,
    entityDisabledByGroup,
    planarGraphs,
  } = input;

  const foundationSettingsFromConfig = foundationSystemSettingsFromSettingsConfig(foundationSettings);

  const finalizedFoundationComponents = finalizeFoundationComponents(
    foundationComponents,
    foundationSettingsFromConfig,
  );

  const foundationHorizontalAssembliesPaths: RWTable<HalfEdgeToFoundationPath> = {};

  const resolvedFloorSystems = evalFormulas(floorSystems, (v) => optionChoices[v], undefined, entityDisabledByGroup);
  const resolvedFloors = evalFormulas(floors, (v) => optionChoices[v], undefined, entityDisabledByGroup);

  for (const floorId in resolvedFloors) {
    const floor = resolvedFloors[floorId];
    const floorSystemId = floor.parentId;

    if (!shouldProcessFloorWithBoundaries(floor, floorSystemId, resolvedFloorSystems, floorBoundaries)) {
      continue;
    }

    const boundaries = floorBoundaries[floorId];
    if (!boundaries || boundaries.perimeters.length === 0) {
      continue;
    }

    const floorGraph = planarGraphs[floorId];
    if (!floorGraph) {
      console.error(`Floor graph not found for floor ${floorId}`);
      continue;
    }

    const floorPaths = generateFoundationPathsFromFloorPerimeter({
      planarGraphId: floorId,
      planarGraph: floorGraph,
      planarGraphs,
      floor,
      floorBoundaries: boundaries,
      foundationAssemblies,
      finalizedFoundationComponents,
      foundationSettings: foundationSettingsFromConfig,
      datumAbstractions,
    });

    if (Object.keys(floorPaths).length === 0) {
      console.error(`Foundation paths not found for floor ${floorId}`);
      continue;
    }

    foundationHorizontalAssembliesPaths[floorId] = floorPaths;
  }

  return foundationHorizontalAssembliesPaths;
}

/**
 * Generates foundation paths from a foundation's horizontal assembly.
 * This function handles edge-specific overrides from foundationEdgeOverrides.
 */
export function generateFoundationPathsFromFoundationPerimeter({
  planarGraphId,
  planarGraph,
  planarGraphs,
  foundation,
  foundationAssemblies,
  finalizedFoundationComponents,
  foundationSettings,
  datumAbstractions,
  polygonIndex,
  mirror,
  mirrorTranslationX,
}: {
  planarGraphId: string;
  planarGraph: PolygonPlanarGraph;
  planarGraphs: Table<PolygonPlanarGraph>;
  foundation: Foundation;
  foundationAssemblies: Table<FoundationAssemblyEntity>;
  finalizedFoundationComponents: Table<FoundationComponentEntity>;
  foundationSettings: FoundationSystemSettings;
  datumAbstractions: Table<DatumAbstraction>;
  polygonIndex: number;
  mirror: boolean;
  mirrorTranslationX: number;
}): HalfEdgeToFoundationPath {
  const horizontalAssembliesFoundationPaths: HalfEdgeToFoundationPath = {};
  const { planarGraph: graph } = planarGraph;

  // Foundation extends HorizontalFoundationAssembly and has foundationAssemblyId property
  // Using type assertion because TypeScript doesn't always recognize inherited properties
  const assemblyId =
    (foundation as { foundationAssemblyId?: string }).foundationAssemblyId ?? foundationSettings.foundationAssembly;
  const assemblyComponents = assemblyId ? foundationAssemblies[assemblyId] : undefined;

  /**ensure that we have an assembly id and components to build an assembly - there should always be one in the settings*/
  if (!assemblyId || !assemblyComponents) {
    throw new Error(`Foundation assembly id not found for foundation ${foundation.id}`);
  }

  const { perimeter, edgeIds } = foundation;
  if (!perimeter || !edgeIds) {
    return horizontalAssembliesFoundationPaths;
  }

  // Compute the edge ID mapping for the offset perimeter.
  // This maps planar graph edge indices to original edge IDs or transition edge IDs.
  // Transition edges are created when edge offsets are applied, and they should
  // inherit properties from their source edge.
  const foundationEdgeOverrides = (
    foundation as {
      foundationEdgeOverrides?: {
        [key: number]: FoundationAssemblyEntityDatums & { foundationAssemblyId?: string };
      };
    }
  ).foundationEdgeOverrides;

  const { edgeIds: offsetEdgeIds } = foundationPerimeterInfoToUse(
    perimeter,
    edgeIds,
    foundationEdgeOverrides,
    mirror,
    mirrorTranslationX,
    {
      datumAbstractions,
      foundationLateralDatumId: foundation.lateralDatumId,
    },
  );

  // get the face half edges corresponding to the input polygon
  const faceId = planarGraph.polygonToPlanarGraphFace(polygonIndex);
  const faceHalfEdges = graph.faceHalfEdges[faceId];

  // iterate through polygon edges
  const polygonEdges = planarGraph.polygonToPlanarGraph[polygonIndex];
  if (!polygonEdges) {
    return horizontalAssembliesFoundationPaths;
  }

  for (let polygonEdgeIndex = 0; polygonEdgeIndex < polygonEdges.length; polygonEdgeIndex++) {
    const edgeRefs = polygonEdges[polygonEdgeIndex];
    for (const edgeRef of edgeRefs) {
      if (edgeRef.index !== polygonIndex) {
        continue;
      }
      const halfEdgeIndex = faceHalfEdges[edgeRef.edgeIndex];
      const oppositeHalfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);
      if (
        horizontalAssembliesFoundationPaths[halfEdgeIndex] &&
        horizontalAssembliesFoundationPaths[oppositeHalfEdgeIndex]
      ) {
        continue;
      }

      // Get foundationEdgeIndex from the foundation polygon's side (this is the planar graph edge index)
      const planarGraphEdgeIndex = edgeRef.edgeIndex;

      // Get foundationEdgeIndex from the opposite polygon's side
      /**
       * @note We need to get the opposite polygon index and and corresponding floor object to object its override.
       * DO NOT DELETE THE COMMENTS BELOW.
       */
      // const oppositePolygonIndex = planarGraph.halfEdgeIndexToPolygonIndex(oppositeHalfEdgeIndex);
      // const oppositePolygonEdgeRef = planarGraph.halfEdgeIndexToPolygonEdgeRef(oppositeHalfEdgeIndex);
      // const oppositeFoundationEdgeIndex = oppositePolygonEdgeRef?.edgeIndex;
      // get oppositeFoundation from oppositePolygonIndex
      // get oppositeFoundationEdgeOverride from oppositeFoundation
      // FoundationEdgeOverridesTable is indexed by 1-based numeric edge index
      // const oppositeFoundationEdgeOverrideIndex = oppositeFoundationEdgeIndex !== undefined ? oppositeFoundationEdgeIndex + 1 : undefined;
      // const oppositeFoundationEdgeOverride = oppositeFoundationEdgeOverrideIndex
      //   ? oppositeFoundationEdgeOverride?.[oppositeFoundationEdgeOverrideIndex]
      //   : undefined;
      // implement the logic to pick between the foundationEdgeOverride using some priority logic TBD with @abrahamdrechsler
      // and that becomes the preferredOverride. Currently we are not generating horizontal assemblies using multiple polygons
      // so we just the foundationEdgeOverride from the input polygon and that becomes the preferredOverride.

      // Consolidate overrides: prefer foundation polygon's side, fall back to opposite side

      // Get the edge ID for this planar graph edge from the offset perimeter edge IDs.
      // This may be an original edge ID or a transition edge ID (for edges created by offsets).
      const edgeId = offsetEdgeIds[planarGraphEdgeIndex];

      // Determine which original edge's properties to use.
      // For transition edges, inherit properties from the source edge (the edge that had the offset).
      let sourceEdgeId: string;
      if (edgeId !== undefined && isFoundationTransitionEdge(edgeId)) {
        // Transition edge: get the source edge ID to inherit properties from
        sourceEdgeId = getTransitionEdgeSourceId(edgeId) ?? edgeId;
      } else {
        // Regular edge: use its own edge ID
        sourceEdgeId = edgeId ?? "";
      }

      // Find the 1-based index of the source edge in the original edgeIds array.
      // FoundationEdgeOverridesTable is indexed by 1-based numeric edge index.
      const originalEdgeIndex = edgeIds.indexOf(sourceEdgeId);
      const foundationEdgeOverrideIndex = originalEdgeIndex >= 0 ? originalEdgeIndex + 1 : -1;

      // Look up the edge override using the source edge's index
      // Foundation extends HorizontalFoundationAssembly which has foundationEdgeOverrides
      // Using type assertion because TypeScript doesn't always recognize inherited properties
      // FoundationEdgeOverridesTable is { [key: number]: FoundationEdgeOverride }
      const foundationEdgeOverride =
        foundationEdgeOverrideIndex > 0 ? foundationEdgeOverrides?.[foundationEdgeOverrideIndex] : undefined;

      // Consolidate: prefer foundation polygon's override, fall back to opposite side's override
      const preferredOverride = foundationEdgeOverride;
      // Use foundationAssemblyId from edge override if available, otherwise fall back to foundation's foundationAssemblyId or settings
      const edgeAssemblyId = preferredOverride?.foundationAssemblyId;
      if (edgeAssemblyId === NONE_VALUE) {
        // Explicit edge-level "None" sentinel suppresses foundation generation on this edge.
        continue;
      }
      const edgeAssemblyComponents = edgeAssemblyId ? foundationAssemblies[edgeAssemblyId] : undefined;
      // Use edge-specific assembly if available and valid, otherwise use the default assembly
      const finalAssemblyId = edgeAssemblyId && edgeAssemblyComponents ? edgeAssemblyId : assemblyId;
      const finalAssemblyComponents = edgeAssemblyComponents ?? assemblyComponents;

      // Foundation extends HorizontalFoundationAssembly which extends FoundationAssemblyEntityDatums
      // Using type assertion because TypeScript doesn't always recognize inherited properties
      const foundationAsDatums = foundation as unknown as FoundationAssemblyEntityDatums;
      // For freeform foundations, foundation-level datum selections are true overrides and must
      // win over assembly catalog datums. Use foundationAssemblyOverride precedence in
      // makeSolidFoundationAssembly (override > assembly > instance) by passing effective datums here.
      const effectiveDatumOverrides: FoundationAssemblyEntityDatums = {
        topDatumId: preferredOverride?.topDatumId ?? foundationAsDatums.topDatumId,
        bottomDatumId: preferredOverride?.bottomDatumId ?? foundationAsDatums.bottomDatumId,
        lateralDatumId: preferredOverride?.lateralDatumId ?? foundationAsDatums.lateralDatumId,
        brickledgeTopDatumId: preferredOverride?.brickledgeTopDatumId ?? foundationAsDatums.brickledgeTopDatumId,
      };
      const foundationAssembly = makeSolidFoundationAssembly({
        assemblyId: finalAssemblyId,
        foundationAssemblyComponents: finalAssemblyComponents,
        foundationComponents: finalizedFoundationComponents,
        foundationSettings,
        datumAbstractions,
        foundationAssemblyOverride: effectiveDatumOverrides,
      });

      /**
       * @note We need to discuss how we want to handle the part which is derived from the floor paths.
       * "condition" parameter below is arbitrary and should be derived from the floor paths. If it cannot be derived from the floor paths,
       * what should be the appropriate default? Is below value a valid assumption?
       */

      const foundationPath: FoundationPath = {
        halfEdgeIndex,
        planarGraphId,
        assembly: foundationAssembly,
        isolatedAssemblies: [],
        subSystemOffset: 0,
        superSystemOffset: 0,
        condition: defaultHorizontalAssemblyFoundationCondition(foundationSettings),
        isDerivedFromHorizontalAssembly: true,
      };

      horizontalAssembliesFoundationPaths[halfEdgeIndex] = foundationPath;
      horizontalAssembliesFoundationPaths[oppositeHalfEdgeIndex] = foundationPathOpposite(foundationPath, planarGraphs);
    }
  }

  return horizontalAssembliesFoundationPaths;
}

/**
 * Generates foundation paths from a floor's perimeter.
 * This function handles edge-specific overrides from floorEdgeOverrides.
 */
export function generateFoundationPathsFromFloorPerimeter({
  planarGraphId,
  planarGraph,
  planarGraphs,
  floor,
  floorBoundaries,
  foundationAssemblies,
  finalizedFoundationComponents,
  foundationSettings,
  datumAbstractions,
}: {
  planarGraphId: string;
  planarGraph: PolygonPlanarGraph;
  planarGraphs: Table<PolygonPlanarGraph>;
  floor: FloorAssemblyOverride;
  floorBoundaries: GeneratedFloorBoundaries;
  foundationAssemblies: Table<FoundationAssemblyEntity>;
  finalizedFoundationComponents: Table<FoundationComponentEntity>;
  foundationSettings: ReturnType<typeof foundationSystemSettingsFromSettingsConfig>;
  datumAbstractions: Table<DatumAbstraction>;
}): HalfEdgeToFoundationPath {
  const foundationPaths: HalfEdgeToFoundationPath = {};
  const { planarGraph: graph } = planarGraph;

  // Use foundation assembly from floor's foundationAssemblyId if available, otherwise use default from settings
  const floorFoundationAssemblyId: string | undefined =
    "foundationAssemblyId" in floor && typeof floor.foundationAssemblyId === "string"
      ? floor.foundationAssemblyId
      : undefined;
  const assemblyId: string = floorFoundationAssemblyId ?? foundationSettings.foundationAssembly;
  const assemblyComponents = assemblyId ? foundationAssemblies[assemblyId] : undefined;

  if (!assemblyId || !assemblyComponents) {
    return foundationPaths;
  }

  const polygonCount = planarGraph.polygonToPlanarGraph.length;
  const combinedEdgeIds = floorBoundaries.edgeIds;
  const perimeterEdgeCounts = floorBoundaries.perimeterEdgeCounts;

  for (let polygonIndex = 0; polygonIndex < polygonCount; polygonIndex++) {
    const faceId = planarGraph.polygonToPlanarGraphFace(polygonIndex);
    const faceHalfEdges = graph.faceHalfEdges[faceId];
    const polygonEdges = planarGraph.polygonToPlanarGraph[polygonIndex];
    if (!polygonEdges) {
      continue;
    }

    const perimeterEdgeIds = getEdgeIdsForPerimeter(combinedEdgeIds, perimeterEdgeCounts, polygonIndex);

    for (let polygonEdgeIndex = 0; polygonEdgeIndex < polygonEdges.length; polygonEdgeIndex++) {
      const edgeRefs = polygonEdges[polygonEdgeIndex];
      for (const edgeRef of edgeRefs) {
        if (edgeRef.index !== faceId) {
          continue;
        }
        const halfEdgeIndex = faceHalfEdges[edgeRef.edgeIndex];
        const oppositeHalfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);
        if (foundationPaths[halfEdgeIndex] && foundationPaths[oppositeHalfEdgeIndex]) {
          continue;
        }

        const floorEdgeId = perimeterEdgeIds[edgeRef.edgeIndex];
        const floorEdgeOverride =
          floorEdgeId !== undefined ? floorBoundaries.floorEdgeOverrides[floorEdgeId] : undefined;
        // Extract foundationEdgeOverride from the floor edge override
        const foundationEdgeOverride = floorEdgeOverride?.foundationEdgeOverride;

        // Use foundation assembly from floor's foundationAssemblyEntityDatums if available
        let floorFoundationAssemblyEntityDatums: FoundationAssemblyEntityDatums | undefined;
        if ("foundationAssemblyEntityDatums" in floor && floor.foundationAssemblyEntityDatums) {
          floorFoundationAssemblyEntityDatums = floor.foundationAssemblyEntityDatums;
        }

        // Consolidate: prefer edge-specific override, fall back to floor-level properties
        const preferredOverride = foundationEdgeOverride;

        // Use foundationAssemblyId from edge override if available, otherwise fall back to floor's foundationAssemblyId or settings
        const edgeAssemblyId = preferredOverride?.foundationAssemblyId;
        if (edgeAssemblyId === NONE_VALUE) {
          // Explicit edge-level "None" sentinel suppresses foundation generation on this edge.
          continue;
        }
        const edgeAssemblyComponents = edgeAssemblyId ? foundationAssemblies[edgeAssemblyId] : undefined;
        // Use edge-specific assembly if available and valid, otherwise use the default assembly
        const finalAssemblyId =
          edgeAssemblyId && edgeAssemblyComponents ? edgeAssemblyId : floorFoundationAssemblyId ?? assemblyId;
        const finalAssemblyComponents =
          edgeAssemblyComponents ??
          (floorFoundationAssemblyId &&
          typeof floorFoundationAssemblyId === "string" &&
          foundationAssemblies[floorFoundationAssemblyId]
            ? foundationAssemblies[floorFoundationAssemblyId]
            : assemblyComponents);

        if (!finalAssemblyComponents) {
          continue;
        }

        // Use datum overrides from edge override if available, otherwise fall back to floor-level datums
        const floorAsDatums =
          floorFoundationAssemblyEntityDatums ?? (floor as unknown as FoundationAssemblyEntityDatums);
        // For floor-derived foundation paths, floor/edge datums are user overrides and should
        // win over assembly catalog datums. Route through foundationAssemblyOverride precedence.
        const effectiveDatumOverrides: FoundationAssemblyEntityDatums = {
          topDatumId:
            preferredOverride?.topDatumId ??
            floorAsDatums?.topDatumId ??
            ("topDatum" in floor && floor.topDatum ? floor.topDatum : undefined),
          bottomDatumId: preferredOverride?.bottomDatumId ?? floorAsDatums?.bottomDatumId,
          lateralDatumId: preferredOverride?.lateralDatumId ?? floorAsDatums?.lateralDatumId,
          brickledgeTopDatumId: preferredOverride?.brickledgeTopDatumId ?? floorAsDatums?.brickledgeTopDatumId,
        };

        const foundationAssembly = makeSolidFoundationAssembly({
          assemblyId: finalAssemblyId,
          foundationAssemblyComponents: finalAssemblyComponents,
          foundationComponents: finalizedFoundationComponents,
          foundationSettings,
          datumAbstractions,
          foundationAssemblyOverride: effectiveDatumOverrides,
        });

        /**
         * @note We need to discuss how we want to handle the part which is derived from the floor paths.
         * "condition" parameter below is arbitrary and should be derived from the floor paths. If it cannot be derived from the floor paths,
         * what should be the appropriate default? Is below value a valid assumption?
         *
         * @note Forcing subsystem and super system offsets to 0. We will not need to pass in subsystem offset here with these assemblies
         * because they are generated independently via floor system generation. The super system offfset is useful and works when these paths
         * are used in creating slab systems but in this case they are not used in that manner and we want to keep them decoupled.
         */

        const foundationPath: FoundationPath = {
          halfEdgeIndex,
          planarGraphId,
          assembly: foundationAssembly,
          isolatedAssemblies: [],
          subSystemOffset: 0,
          superSystemOffset: 0,
          condition: FoundationCondition.HOUSE_EXTERIOR,
          isDerivedFromHorizontalAssembly: true,
        };

        foundationPaths[halfEdgeIndex] = foundationPath;
        foundationPaths[oppositeHalfEdgeIndex] = foundationPathOpposite(foundationPath, planarGraphs);
      }
    }
  }

  return foundationPaths;
}
